// ============================================================
// Logs.tsx
// ------------------------------------------------------------
// Visar senaste klient-loggar (errors/warnings/info) som skrivs
// av cloudStore.writeLog(). Realtid via onSnapshot.
//
// Hit hamnar t.ex. fel från Re-run AI / Storage-proxy så att
// användaren inte behöver scrolla horisontellt i en dialog.
// ============================================================

import { useEffect, useMemo, useState } from 'react'
import { subscribeLogs, clearAllLogs, type LogEntry, type LogLevel } from '../lib/cloudStore'
import { collection, getDocs, query, where, limit } from 'firebase/firestore'
import { db, auth } from '../firebase'
import { COMPANIES } from '../data/companies'

interface Props {
  onClose: () => void
}

const LEVEL_LABELS: Record<LogLevel, string> = {
  info: 'Info',
  warn: 'Varning',
  error: 'Fel',
}

const LEVEL_COLORS: Record<LogLevel, { bg: string; border: string; text: string }> = {
  info:  { bg: 'rgba(96,165,250,0.10)', border: '#60a5fa', text: '#93c5fd' },
  warn:  { bg: 'rgba(245,158,11,0.10)', border: '#f59e0b', text: '#fbbf24' },
  error: { bg: 'rgba(239,68,68,0.10)',  border: '#ef4444', text: '#fca5a5' },
}

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('sv-SE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  } catch {
    return iso
  }
}

/** Format a log entry as plain text (good for paste into chat / bug reports) */
function entryToText(e: LogEntry): string {
  const lines = [
    fmtTime(e.createdAt) + '  [' + e.level.toUpperCase() + ']  ' + e.source +
      (e.companyId ? ' (' + e.companyId + ')' : ''),
    e.message,
  ]
  if (e.context) {
    lines.push('Context: ' + JSON.stringify(e.context))
  }
  return lines.join('\n')
}

/** Copy plain text to clipboard. Returns true on success. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
    // Fallback for non-https contexts
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

export function Logs({ onClose }: Props) {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<LogLevel | 'all'>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [clearing, setClearing] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [diagBusy, setDiagBusy] = useState(false)
  const [diagResult, setDiagResult] = useState<string | null>(null)

  function flashCopied(key: string) {
    setCopiedKey(key)
    setTimeout(() => setCopiedKey((cur) => cur === key ? null : cur), 1500)
  }

  async function runDiagnostic() {
    setDiagBusy(true)
    setDiagResult(null)
    const lines: string[] = []
    const log = (s: string) => { lines.push(s); setDiagResult(lines.join('\n')) }

    try {
      const user = auth.currentUser
      if (!user) { log('FAIL: inte inloggad'); setDiagBusy(false); return }
      const idToken = await user.getIdToken()
      log('User: ' + user.email + ' uid=' + user.uid)

      // 1. Hitta första receipt med storagePath över alla bolag
      let testPath: string | null = null
      let testFilename: string | null = null
      for (const co of COMPANIES) {
        const colRef = collection(db, 'users', user.uid, 'companies', co.id, 'receipts')
        const q = query(colRef, where('year', '==', new Date().getFullYear()), limit(20))
        const snap = await getDocs(q)
        for (const d of snap.docs) {
          const data = d.data() as { storagePath?: string; filename?: string }
          if (data.storagePath && data.storagePath.length > 0) {
            testPath = data.storagePath
            testFilename = data.filename ?? '(okänt)'
            log('Test-receipt: ' + co.name + ' / ' + testFilename)
            break
          }
        }
        if (testPath) break
      }
      if (!testPath) {
        log('FAIL: hittade inget receipt med storagePath')
        setDiagBusy(false); return
      }

      // 2. /api/debug-fetch?target=env
      log('\n[1] GET /api/debug-fetch?target=env')
      const r1 = await fetch('/api/debug-fetch?target=env')
      log('  HTTP ' + r1.status + ' | ' + (await r1.text()).slice(0, 200))

      // 3. /api/debug-fetch?target=xai-text
      log('\n[2] GET /api/debug-fetch?target=xai-text  (xAI text-only)')
      const t2 = Date.now()
      const r2 = await fetch('/api/debug-fetch?target=xai-text')
      const r2body = await r2.text()
      log('  HTTP ' + r2.status + ' (' + (Date.now() - t2) + 'ms) | ' + r2body.slice(0, 300))

      // 4. /api/storage-extract-test (auth + GCS + storage fetch + base64, NO xAI)
      log('\n[3] POST /api/storage-extract-test  (auth+GCS+storage+base64, INGEN xAI)')
      const t3 = Date.now()
      const r3 = await fetch('/api/storage-extract-test', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storagePath: testPath }),
      })
      const r3ct = r3.headers.get('content-type') || ''
      const r3body = await r3.text()
      log('  HTTP ' + r3.status + ' CT=' + r3ct + ' (' + (Date.now() - t3) + 'ms)')
      log('  ' + r3body.slice(0, 600))

      // 5. /api/storage-extract?dryRun=1 (full flow MEN HOPPAR ÖVER xAI-fetch)
      log('\n[4] POST /api/storage-extract?dryRun=1  (FULL flow MEN UTAN faktisk xAI-fetch)')
      const t4 = Date.now()
      const r4 = await fetch('/api/storage-extract?dryRun=1', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storagePath: testPath }),
      })
      const r4ct = r4.headers.get('content-type') || ''
      const r4body = await r4.text()
      log('  HTTP ' + r4.status + ' CT=' + r4ct + ' (' + (Date.now() - t4) + 'ms)')
      log('  ' + r4body.slice(0, 600))

      // NY: testa skicka samma PDF-body till httpbin istället för xAI
      log('\n[5] POST /api/storage-extract?targetUrl=httpbin  (skicka 153KB body till httpbin istället för xAI)')
      const t5h = Date.now()
      const r5h = await fetch('/api/storage-extract?targetUrl=httpbin', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storagePath: testPath }),
      })
      const r5hct = r5h.headers.get('content-type') || ''
      const r5hbody = await r5h.text()
      log('  HTTP ' + r5h.status + ' CT=' + r5hct + ' (' + (Date.now() - t5h) + 'ms)')
      log('  ' + r5hbody.slice(0, 400))

      // 6. /api/storage-extract?fakeMime=image/jpeg (skickar PDF-bytes men säger "image/jpeg" till xAI)
      log('\n[6] POST /api/storage-extract?fakeMime=image/jpeg  (PDF-bytes men säger image/jpeg till xAI)')
      const t5 = Date.now()
      const r5 = await fetch('/api/storage-extract?fakeMime=image/jpeg', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storagePath: testPath }),
      })
      const r5ct = r5.headers.get('content-type') || ''
      const r5body = await r5.text()
      log('  HTTP ' + r5.status + ' CT=' + r5ct + ' (' + (Date.now() - t5) + 'ms)')
      log('  ' + r5body.slice(0, 600))

      // 7. /api/storage-extract  (FULL flow inkl xAI — det som faktiskt failade)
      log('\n[7] POST /api/storage-extract  (FULL flow inkl xAI — det som verkligen kraschar)')
      const t6 = Date.now()
      const r6 = await fetch('/api/storage-extract', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storagePath: testPath }),
      })
      const r6ct = r6.headers.get('content-type') || ''
      const r6body = await r6.text()
      log('  HTTP ' + r6.status + ' CT=' + r6ct + ' (' + (Date.now() - t6) + 'ms)')
      log('  ' + r6body.slice(0, 500))

      log('\n=== DIAGNOSTIK KLAR ===')
    } catch (e) {
      log('UNCAUGHT: ' + (e as Error).message)
    } finally {
      setDiagBusy(false)
    }
  }

  useEffect(() => {
    let unsub: (() => void) | null = null
    try {
      unsub = subscribeLogs((entries) => {
        setLogs(entries)
        setLoading(false)
      }, 500)
    } catch (e) {
      console.error('subscribeLogs failed:', e)
      setLoading(false)
    }
    return () => { unsub?.() }
  }, [])

  const filtered = useMemo(() => {
    if (filter === 'all') return logs
    return logs.filter((l) => l.level === filter)
  }, [logs, filter])

  const counts = useMemo(() => {
    const c = { error: 0, warn: 0, info: 0 }
    for (const l of logs) c[l.level]++
    return c
  }, [logs])

  async function handleClear() {
    if (!confirm('Radera alla ' + logs.length + ' logg-rader? Detta kan inte ångras.')) return
    setClearing(true)
    try {
      const n = await clearAllLogs()
      console.log('Cleared', n, 'log entries')
    } catch (e) {
      alert('Fel vid rensning: ' + (e as Error).message)
    } finally {
      setClearing(false)
    }
  }

  return (
    <div className="shell shell--wide">
      <header className="topbar">
        <div className="topbar__brand">Loggar</div>
        <div className="topbar__right">
          <button className="logout-btn" onClick={onClose}>{'← Tillbaka'}</button>
        </div>
      </header>

      <div style={{ padding: '0 1rem 2rem' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem',
        }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <FilterChip label={'Alla (' + logs.length + ')'} active={filter === 'all'} onClick={() => setFilter('all')} />
            <FilterChip label={'Fel (' + counts.error + ')'} active={filter === 'error'} onClick={() => setFilter('error')} color="#ef4444" />
            <FilterChip label={'Varningar (' + counts.warn + ')'} active={filter === 'warn'} onClick={() => setFilter('warn')} color="#f59e0b" />
            <FilterChip label={'Info (' + counts.info + ')'} active={filter === 'info'} onClick={() => setFilter('info')} color="#60a5fa" />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={runDiagnostic}
              disabled={diagBusy}
              style={{
                background: diagBusy ? 'rgba(245,158,11,0.15)' : 'rgba(167,139,250,0.15)',
                color: diagBusy ? '#f59e0b' : '#a78bfa',
                border: '1px solid ' + (diagBusy ? '#f59e0b' : '#a78bfa'),
                borderRadius: 6, padding: '0.45rem 0.9rem', fontSize: '0.85rem',
                cursor: diagBusy ? 'wait' : 'pointer',
              }}
            >{diagBusy ? '⏳ Kör...' : '🔬 Kör diagnostik'}</button>
            <button
              onClick={async () => {
                const text = filtered.map(entryToText).join('\n\n---\n\n')
                const ok = await copyText(text)
                if (ok) flashCopied('all')
                else alert('Kopiering misslyckades — markera texten manuellt istället.')
              }}
              disabled={filtered.length === 0}
              style={{
                background: 'rgba(96,165,250,0.15)', color: '#60a5fa',
                border: '1px solid #60a5fa', borderRadius: 6,
                padding: '0.45rem 0.9rem', fontSize: '0.85rem',
                cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
                opacity: filtered.length === 0 ? 0.5 : 1,
              }}
            >
              {copiedKey === 'all' ? '✓ Kopierat!' : '📋 Kopiera ' + (filter === 'all' ? 'alla' : filter) + ' (' + filtered.length + ')'}
            </button>
            <button
              onClick={handleClear}
              disabled={clearing || logs.length === 0}
              style={{
                background: 'rgba(239,68,68,0.15)', color: '#ef4444',
                border: '1px solid #ef4444', borderRadius: 6,
                padding: '0.45rem 0.9rem', fontSize: '0.85rem',
                cursor: clearing || logs.length === 0 ? 'not-allowed' : 'pointer',
                opacity: clearing || logs.length === 0 ? 0.5 : 1,
              }}
            >
              {clearing ? 'Rensar...' : '🗑️ Rensa alla'}
            </button>
          </div>
        </div>

        {diagResult && (
          <div style={{
            marginBottom: '1rem',
            border: '1px solid #a78bfa', borderRadius: 8,
            background: 'rgba(167,139,250,0.05)',
            padding: '0.75rem 1rem',
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '0.5rem',
            }}>
              <strong style={{ color: '#a78bfa' }}>🔬 Diagnostik-resultat</strong>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={async () => {
                    const ok = await copyText(diagResult)
                    if (ok) flashCopied('diag')
                  }}
                  style={{
                    background: copiedKey === 'diag' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)',
                    color: copiedKey === 'diag' ? '#10b981' : 'var(--text)',
                    border: '1px solid ' + (copiedKey === 'diag' ? '#10b981' : 'var(--border)'),
                    borderRadius: 4, padding: '0.25rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer',
                  }}
                >{copiedKey === 'diag' ? '✓ Kopierat' : '📋 Kopiera'}</button>
                <button
                  onClick={() => setDiagResult(null)}
                  style={{
                    background: 'none', border: '1px solid var(--border)',
                    borderRadius: 4, padding: '0.25rem 0.6rem', fontSize: '0.75rem',
                    color: 'var(--text-muted)', cursor: 'pointer',
                  }}
                >✕ Stäng</button>
              </div>
            </div>
            <pre style={{
              margin: 0, padding: '0.5rem',
              background: 'rgba(0,0,0,0.3)', borderRadius: 4,
              fontSize: '0.75rem', color: 'var(--text)',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: '30rem', overflowY: 'auto',
            }}>{diagResult}</pre>
          </div>
        )}

        {loading && (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>
            Laddar loggar...
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{
            border: '1px solid var(--border)', borderRadius: 8,
            padding: '2rem', textAlign: 'center', color: 'var(--text-muted)',
          }}>
            {logs.length === 0
              ? 'Inga loggar än. När något fel inträffar (t.ex. AI-batch) hamnar det här.'
              : 'Inga loggar matchar filtret.'}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{
            border: '1px solid var(--border)', borderRadius: 8,
            background: 'var(--bg-card)',
          }}>
            {filtered.map((entry, idx) => {
              const c = LEVEL_COLORS[entry.level]
              const isExpanded = expandedId === entry.id
              return (
                <div
                  key={entry.id}
                  style={{
                    borderBottom: idx === filtered.length - 1 ? 'none' : '1px solid var(--border)',
                    padding: '0.6rem 0.9rem',
                    cursor: entry.context ? 'pointer' : 'default',
                  }}
                  onClick={() => entry.context && setExpandedId(isExpanded ? null : entry.id)}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    gap: '0.5rem',
                  }}>
                    <div style={{
                      display: 'flex', gap: '0.5rem', alignItems: 'center',
                      flexWrap: 'wrap',
                    }}>
                      <span style={{
                        background: c.bg, border: '1px solid ' + c.border, color: c.text,
                        padding: '0.1rem 0.45rem', borderRadius: 4,
                        fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
                      }}>{LEVEL_LABELS[entry.level]}</span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {fmtTime(entry.createdAt)}
                      </span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {entry.source}{entry.companyId ? ' · ' + entry.companyId : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                      <button
                        onClick={async (ev) => {
                          ev.stopPropagation()
                          const ok = await copyText(entryToText(entry))
                          if (ok) flashCopied(entry.id)
                        }}
                        title="Kopiera den här logg-raden"
                        style={{
                          background: copiedKey === entry.id ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.04)',
                          color: copiedKey === entry.id ? '#10b981' : 'var(--text-muted)',
                          border: '1px solid ' + (copiedKey === entry.id ? '#10b981' : 'var(--border)'),
                          borderRadius: 4, padding: '0.1rem 0.45rem',
                          fontSize: '0.7rem', cursor: 'pointer',
                        }}
                      >{copiedKey === entry.id ? '✓ Kopierat' : '📋 Kopiera'}</button>
                      {entry.context && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{
                    marginTop: '0.3rem', fontSize: '0.85rem',
                    color: 'var(--text)', wordBreak: 'break-word',
                  }}>
                    {entry.message}
                  </div>
                  {isExpanded && entry.context && (
                    <pre style={{
                      marginTop: '0.5rem', padding: '0.5rem',
                      background: 'rgba(0,0,0,0.3)', borderRadius: 4,
                      fontSize: '0.7rem', overflow: 'auto', maxHeight: '20rem',
                      color: 'var(--text-muted)',
                    }}>{JSON.stringify(entry.context, null, 2)}</pre>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function FilterChip({
  label, active, onClick, color,
}: {
  label: string; active: boolean; onClick: () => void; color?: string
}) {
  const borderC = active ? (color ?? '#10b981') : 'var(--border)'
  const bgC = active ? (color ?? '#10b981') + '20' : 'transparent'
  return (
    <button
      onClick={onClick}
      style={{
        background: bgC, color: active ? (color ?? '#10b981') : 'var(--text)',
        border: '1px solid ' + borderC, borderRadius: 6,
        padding: '0.35rem 0.7rem', fontSize: '0.82rem',
        fontWeight: active ? 600 : 400, cursor: 'pointer',
      }}
    >{label}</button>
  )
}

export default Logs
